import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  InputAdornment,
  useTheme,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  CalculateOutlined,
  CheckCircleOutline,
  CreditScoreOutlined,
  ReceiptLongOutlined,
  InfoOutlined,
  ShieldOutlined,
  WarningAmberOutlined,
  ArrowForwardOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import {
  walletQuery,
  walletMutate,
  CALCULATE_CHARGES_QUERY,
  ACTIVE_FEE_SCHEDULE_QUERY,
  FEE_TIERS_QUERY,
} from "../../data/WalletService/walletGraphQL";
import { formatKES } from "./WalletBalance";

const QUICK_DISBURSEMENT_AMOUNTS = [1000, 3000, 5000, 10000, 25000, 50000];

const ChargePreview = ({ companyId, availableBalance = 0, overdraftHeadroom = 0 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [amount, setAmount] = useState("5000");
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [tiers, setTiers] = useState([]);

  // Load fee schedule tiers — try GQL activeFeeSchedule first, then DB fallback
  useEffect(() => {
    async function loadTiers() {
      // 1. GQL: get active schedule → then get its tiers
      try {
        const schedData = await walletQuery(ACTIVE_FEE_SCHEDULE_QUERY);
        if (schedData?.activeFeeSchedule?.id) {
          const tierData = await walletQuery(FEE_TIERS_QUERY, {
            scheduleId: parseInt(schedData.activeFeeSchedule.id, 10),
          });
          if (tierData?.feeTiers && tierData.feeTiers.length > 0) {
            setTiers(
              tierData.feeTiers.map((t) => ({
                amount_from: t.amountFrom,
                amount_to: t.amountTo,
                sababisha_charge: t.sababishaCharge,
                mpesa_charge: t.mpesaCharge,
              }))
            );
            return;
          }
        }
      } catch (gqlErr) {
        console.warn("Could not load fee tiers via GQL:", gqlErr);
      }
    }
    loadTiers();
  }, []);

  // Calculate charge breakdown — GraphQL first, REST fallback, then local computation
  const calculateCharges = useCallback(async (testAmount) => {
    const num = parseFloat(testAmount);
    if (!num || num <= 0) {
      setCalculation(null);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
    };

    try {
      let result = null;

      // 1. GraphQL calculateCharges query (amount only — no companyId arg)
      try {
        const gqlData = await walletQuery(CALCULATE_CHARGES_QUERY, {
          amount: num,
        });
        if (gqlData?.calculateCharges) {
          const c = gqlData.calculateCharges;
          result = {
            amount: Number(c.amount ?? num),
            sababishaCharge: Number(c.sababishaCharge || 0),
            mpesaCharge: Number(c.mpesaCharge || 0),
            totalCharge: Number(c.totalCharge || 0),
            totalDebited: Number(c.totalDebited ?? (num + Number(c.totalCharge || 0))),
            tier: c.tier || `Tier #${c.tierId || ""}`,
            scheduleVersion: c.scheduleVersion || `Schedule #${c.scheduleId || ""}`,
          };
        }
      } catch (gqlErr) {
        console.warn("calculateCharges GQL failed:", gqlErr);
      }

      if (!result) {
        // Local computation from DB/GQL tiers
        let matchedTier = null;
        if (tiers.length > 0) {
          matchedTier = tiers
            .filter((t) => Number(t.amount_from) <= num)
            .sort((a, b) => Number(b.amount_from) - Number(a.amount_from))[0];
        }

        if (matchedTier) {
          const s = Number(matchedTier.sababisha_charge || 0);
          const m = Number(matchedTier.mpesa_charge || 0);
          result = {
            amount: num,
            sababishaCharge: s,
            mpesaCharge: m,
            totalCharge: s + m,
            totalDebited: num + s + m,
            tier: `${matchedTier.amount_from} - ${matchedTier.amount_to}`,
            scheduleVersion: "Database Fee Schedule",
          };
        } else {
          result = null;
        }
      }

      setCalculation(result);
    } catch (err) {
      console.error("Failed calculating charges:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, tiers]);

  useEffect(() => {
    calculateCharges(amount);
  }, [amount, calculateCharges]);

  const numAmount = parseFloat(amount) || 0;
  const totalRequired = calculation ? calculation.totalDebited : numAmount;
  const coveredByWallet = Math.min(availableBalance, totalRequired);
  const shortfall = Math.max(0, totalRequired - availableBalance);
  const coveredByOverdraft = Math.min(overdraftHeadroom, shortfall);
  const unfundedShortfall = Math.max(0, shortfall - overdraftHeadroom);

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Header bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            p: 1.2,
            borderRadius: 2,
            backgroundColor: "rgba(23, 102, 162, 0.12)",
            color: "#1766a2",
            display: "flex",
          }}
        >
          <CalculateOutlined fontSize="medium" />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
            Pre-Disbursement Charge Transparency Preview
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[400] }}>
            Preview exact Sababisha and Safaricom charge splits before disbursing a loan.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left: Input Form */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100], mb: 2 }}>
                Enter Loan Principal
              </Typography>

              <TextField
                fullWidth
                label="Disbursement Amount (KES)"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontWeight: 700, color: colors.grey[300] }}>KES</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2.5 }}
              />

              <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                Quick Amounts:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1, mb: 3 }}>
                {QUICK_DISBURSEMENT_AMOUNTS.map((amt) => (
                  <Chip
                    key={amt}
                    label={`KES ${amt.toLocaleString()}`}
                    onClick={() => setAmount(String(amt))}
                    variant={amount === String(amt) ? "filled" : "outlined"}
                    color={amount === String(amt) ? "primary" : "default"}
                    sx={{ fontWeight: 600, cursor: "pointer" }}
                  />
                ))}
              </Box>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}`,
                }}
              >
                <Typography variant="caption" sx={{ color: colors.grey[400], display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ShieldOutlined sx={{ fontSize: 14 }} />
                  Tariff Guarantee:
                </Typography>
                <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mt: 0.5 }}>
                  Charges are snapshotted at escrow time into the disbursement record (fee_tier_id, sababisha_charge, mpesa_charge). No fee is charged if B2C fails.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Calculated Breakdown */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                  Charge Breakdown & Escrow Requirement
                </Typography>
                {calculation && (
                  <Chip
                    label={`Tier Band: ${calculation.tier}`}
                    size="small"
                    sx={{ fontWeight: 700, backgroundColor: "rgba(23,102,162,0.12)", color: "#1766a2" }}
                  />
                )}
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : calculation ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 1.5, borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Sababisha Charge (S)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#1766a2", mt: 0.5 }}>
                          {formatKES(calculation.sababishaCharge)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 1.5, borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Safaricom Charge (M)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#e65100", mt: 0.5 }}>
                          {formatKES(calculation.mpesaCharge)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 1.5, borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Total Transaction Fee
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mt: 0.5 }}>
                          {formatKES(calculation.totalCharge)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Summary Box */}
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: isDark ? "rgba(0,0,0,0.3)" : "#f1f5f9",
                      mb: 2.5,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        Principal Disbursed to Borrower:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatKES(calculation.amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        Total Fees Debited (S + M):
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                        +{formatKES(calculation.totalCharge)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Total Required in Escrow:
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#22bf72" }}>
                        {formatKES(calculation.totalDebited)}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Funding Coverage Status */}
                  {unfundedShortfall === 0 ? (
                    <Alert
                      severity="success"
                      icon={<CheckCircleOutline fontSize="inherit" />}
                      sx={{ borderRadius: 2 }}
                    >
                      <AlertTitle sx={{ fontWeight: 700 }}>Disbursement Can Proceed</AlertTitle>
                      {shortfall === 0 ? (
                        `Fully covered by available wallet balance (${formatKES(availableBalance)} available).`
                      ) : (
                        `Shortfall of ${formatKES(shortfall)} will be automatically drawn from your Overdraft Facility (${formatKES(overdraftHeadroom)} headroom available).`
                      )}
                    </Alert>
                  ) : (
                    <Alert
                      severity="error"
                      icon={<WarningAmberOutlined fontSize="inherit" />}
                      sx={{ borderRadius: 2 }}
                    >
                      <AlertTitle sx={{ fontWeight: 700 }}>Insufficient Funds</AlertTitle>
                      Need {formatKES(calculation.totalDebited)}. Available wallet ({formatKES(availableBalance)}) + Overdraft headroom ({formatKES(overdraftHeadroom)}) leaves an unfunded deficit of {formatKES(unfundedShortfall)}. Please top up your wallet.
                    </Alert>
                  )}
                </>
              ) : (
                <Box sx={{ py: 1 }}>
                  {numAmount > 0 ? (
                    <Alert
                      severity="warning"
                      icon={<WarningAmberOutlined fontSize="inherit" />}
                      sx={{ borderRadius: 2 }}
                    >
                      <AlertTitle sx={{ fontWeight: 700 }}>Fee Schedule Unavailable</AlertTitle>
                      Unable to compute charges: active fee schedule and tiers could not be retrieved. Please configure fee schedule tiers for this company.
                    </Alert>
                  ) : (
                    <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                      Please enter a valid disbursement amount to view the fee calculation.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChargePreview;
